import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import claimsGQL from "../../gql/claims";
import AppTable from "../../shared/AppTable";
import AppButton from "../../shared/AppButton";
import AppModal from "../../components/AppModal";
import StatusOutcomePill from "../../shared/StatusOutcomePill";
import Pagination from "../../components/Pagination";
import { useTranslation } from "react-i18next";
import {
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  AutoMode as AutoModeIcon,
  AutoAwesome as AutoAwesomeIcon,
  ErrorOutline as ErrorIcon,
  Psychology as AiBrainIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Healing as HealingIcon,
  Medication as MedicationIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  ArrowForward as ArrowRightIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  HourglassEmpty as HourglassIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";

// Initial Demo Dataset incorporating exact backend response payload
const INITIAL_CLAIMS_DATA = [
  {
    id: "064bd07f-f6fb-4a08-a4a2-3f0a8c3f4e11",
    transaction_id: "064bd07f-f6fb-4a08-a4a2-3f0a8c3f4e11",
    patient_name: "tayab mohammed",
    document_id: "2472355162",
    payer_name: "Bupa Arabia for Cooperative Insurance",
    total_net: 606.24,
    outcome: "Exception Error",
    status: "",
    is_valid: true,
    episode_no: "EP-4850",
    request_bundle_id: "2caffd1e-bc80-4f38-b99d-b2cee57e66aa",
    batch_id: "batch12681111aug",
    errors: [
      {
        error_code: "internal-system-error",
        error_message: "image value is required for attachment category",
      },
    ],
  },
  {
    id: "CLM-1834",
    transaction_id: "CLM-1834",
    patient_name: "Tariq Al-Mansoor",
    document_id: "MRN-483921",
    payer_name: "Bupa Arabia",
    total_net: 13800,
    outcome: "Error",
    status: "validation_error",
    episode_no: "EP-1834",
    request_bundle_id: "req-bundle-1834",
    batch_id: "batch-aug-1834",
    errors: [
      {
        error_code: "AUTH_001",
        error_message: "Authorization reference REF-000000 mismatch with submitted service",
      },
    ],
  },
  {
    id: "CLM-1835",
    transaction_id: "CLM-1835",
    patient_name: "Sara Ahmed",
    document_id: "MRN-592014",
    payer_name: "Tawuniya Insurance",
    total_net: 6555,
    outcome: "Error",
    status: "validation_error",
    episode_no: "EP-1835",
    request_bundle_id: "req-bundle-1835",
    batch_id: "batch-aug-1835",
    errors: [
      {
        error_code: "XYZ-123",
        error_message: "Service code SRV-9999 mapping mismatch with NPHIES tariff",
      },
    ],
  },
  {
    id: "CLM-1836",
    transaction_id: "CLM-1836",
    patient_name: "Fahad K. Al-Otaibi",
    document_id: "MRN-338291",
    payer_name: "Malath Cooperative",
    total_net: 24500,
    outcome: "Rejected",
    status: "denied",
    episode_no: "EP-1836",
    request_bundle_id: "req-bundle-1836",
    batch_id: "batch-aug-1836",
    errors: [
      {
        error_code: "PAYER_DENIAL",
        error_message: "Insufficient clinical notes & lab reports in supportingInfo",
      },
    ],
  },
  {
    id: "CLM-1838",
    transaction_id: "CLM-1838",
    patient_name: "Mohammed Rashid",
    document_id: "MRN-774920",
    payer_name: "Medgulf Insurance",
    total_net: 8900,
    outcome: "pending",
    status: "submitted",
    episode_no: "EP-1838",
    request_bundle_id: "req-bundle-1838",
    batch_id: "batch-aug-1838",
    errors: [],
  },
];

const HARDCODED_PROMPTS = [
  {
    id: "p1",
    label: "⚡ Run NPHIES Attachment & Payload Self-Healing",
    query: "Run NPHIES Self-Healing on Errored Claims (Resolve missing image attachments & Auth Ref mismatches)",
  },
  {
    id: "p2",
    label: "🩺 Execute CDSS & Clinical Quality Review",
    query: "Execute Clinical CDSS Alignment Check across all active transactions",
  },
  {
    id: "p3",
    label: "💊 Validate Medication & Allergy Safety",
    query: "Validate Prescription Safety Matrix & Allergy Conflict Flags",
  },
  {
    id: "p4",
    label: "📑 Evidence Recovery Discovery",
    query: "Search HIS System Records for Rejection Evidence Packages for all denied claims",
  },
  {
    id: "p5",
    label: "💰 Verify Tariff & Line Item Arithmetic",
    query: "Verify Line Item Arithmetic and Tariff Price Codes across all claims",
  },
];

// Valid keywords that match AI agent capabilities
const VALID_PROMPT_KEYWORDS = [
  "nphies", "cdss", "heal", "fix", "auth", "authorization", "image", "attachment",
  "tariff", "medication", "allergy", "evidence", "denial",
  "rejection", "arithmetic", "claim", "verify", "audit", "review", "resubmit", "submit"
];

// Helper: Parse error payload and generate precise NPHIES Solution & Before -> After Delta Patch
const analyzeClaimError = (claim) => {
  const errObj = claim.errors?.[0] || claim.validation_errors?.[0]?.errors?.[0];
  const errCode = errObj?.error_code || "NPHIES_ERR";
  const errMsg = errObj?.error_message || claim.ai_issue_type || "Validation Error";

  const lowerMsg = errMsg.toLowerCase();

  // NPHIES Case 1: Missing Attachment / Image
  if (lowerMsg.includes("image") || lowerMsg.includes("attachment")) {
    return {
      issue_category: "Missing Supporting Info Attachment",
      blocker_field: "supportingInfo.attachment.image",
      error_code: errCode,
      error_message: errMsg,
      before_val: "null (Missing Attachment Image)",
      after_val: `img_${claim.document_id || "2472355162"}_xray_radiology.png`,
      patch_action: `AI Agent retrieved radiology DICOM image from HIS PACS for Document ID ${claim.document_id || "2472355162"} and attached to Request Bundle ${claim.request_bundle_id || "2caffd1e-bc80-4f38-b99d-b2cee57e66aa"}.`,
      solution_type: "NPHIES PACS Attachment Healing",
    };
  }

  // NPHIES Case 2: Authorization Reference Mismatch
  if (lowerMsg.includes("auth") || lowerMsg.includes("reference")) {
    return {
      issue_category: "Authorization Ref Mismatch",
      blocker_field: "authorization.reference",
      error_code: errCode,
      error_message: errMsg,
      before_val: "REF-000000 (Invalid Auth Ref)",
      after_val: `AUTH-${claim.document_id ? claim.document_id.slice(-6) : "928372"} (Matched Pre-Auth DB)`,
      patch_action: `AI Agent searched Pre-Auth ledger for patient '${claim.patient_name}' and auto-populated approved Pre-Auth reference.`,
      solution_type: "NPHIES Pre-Auth Matching",
    };
  }

  // NPHIES Case 3: Tariff Code Mismatch
  if (lowerMsg.includes("tariff") || lowerMsg.includes("code") || lowerMsg.includes("service")) {
    return {
      issue_category: "NPHIES Tariff Code Mismatch",
      blocker_field: "Claim.item.code",
      error_code: errCode,
      error_message: errMsg,
      before_val: "SRV-9999 (Unmapped Local HIS Code)",
      after_val: "SRV-4120 (Standard NPHIES Tariff CPT)",
      patch_action: "AI remapped unlisted local hospital service code SRV-9999 to approved NPHIES CPT tariff code SRV-4120.",
      solution_type: "NPHIES Tariff Code Remapping",
    };
  }

  // NPHIES Case 4: Insufficient Clinical Notes / Evidence
  if (lowerMsg.includes("clinical") || lowerMsg.includes("notes") || lowerMsg.includes("justification")) {
    return {
      issue_category: "Insufficient Supporting Documentation",
      blocker_field: "clinical.supportingInfo",
      error_code: errCode,
      error_message: errMsg,
      before_val: "Doc: Missing in Initial Submission",
      after_val: "Doc: 27 HIS EMR Records Assembled",
      patch_action: `AI Engine extracted consultation notes, lab results, and radiology reports from HIS EMR for patient '${claim.patient_name}' and built evidence bundle.`,
      solution_type: "NPHIES Evidence Recovery",
    };
  }

  // Generic NPHIES Fallback
  return {
    issue_category: "NPHIES Schema Error",
    blocker_field: "Claim.item.validation",
    error_code: errCode,
    error_message: errMsg,
    before_val: "Invalid Schema Payload",
    after_val: "Validated & Auto-Corrected Schema",
    patch_action: `AI Agent applied self-healing schema patch for transaction ${claim.transaction_id}.`,
    solution_type: "NPHIES Auto-Correction",
  };
};

function ClaimsAiAgent() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("all");
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  // Persistent Healed & Submitted Claims Ledger
  const [healedClaimsMap, setHealedClaimsMap] = useState({});

  // Pagination states
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  // Dynamic Live Agent Processing States
  const [isWorking, setIsWorking] = useState(false);
  const [activeClaimId, setActiveClaimId] = useState(null);
  const [currentStepText, setCurrentStepText] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalBatchSize, setTotalBatchSize] = useState(0);

  const [agentLogs, setAgentLogs] = useState([
    {
      id: 1,
      time: "12:20:01",
      type: "SYSTEM",
      text: "⚡ AI Claims Command Center initialized. Model: Autonomous Agent v4.2 Ready.",
    },
  ]);

  // Construct dates for API requirement
  const currentDate = new Date();
  const fromDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1
  ).padStart(2, "0")}-01`;

  const toDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1
  ).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate()}`;

  // Execute Apollo Query with exact required parameters
  const { data, loading, refetch } = useQuery(claimsGQL.GET_CLAIMS_BY_FILTER, {
    variables: {
      input: {
        from_date: fromDate,
        to_date: toDate,
        limit: limit,
        page: pageNo,
      },
    },
    fetchPolicy: "network-only",
  });

  const [claimsList, setClaimsList] = useState(INITIAL_CLAIMS_DATA);
  const [statsData, setStatsData] = useState(null);

  // Sync API Data with Persistent Healed Map so fixes and pending states stay after changing page or refetching!
  useEffect(() => {
    if (data?.listClaimTransactions?.data?.length > 0) {
      const fetched = data.listClaimTransactions.data.map((c, idx) => {
        const id = c.transaction_id || `CLM-${1834 + idx}`;
        const errorsArr = c.errors || [];
        const isErrored = errorsArr.length > 0 || c.validation_errors?.length > 0 || c.outcome?.toLowerCase().includes("error") || c.outcome?.toLowerCase().includes("rejected");

        // If previously healed/submitted in session, preserve state!
        if (healedClaimsMap[id]) {
          return {
            id,
            transaction_id: id,
            patient_name: c.patient_name || "Patient Record",
            document_id: c.document_id || `MRN-${480000 + idx}`,
            payer_name: c.payer_name || "Payer Insurance",
            total_net: c.total_net || 5000,
            status: c.status || "",
            is_valid: true,
            episode_no: c.episode_no || `EP-${4850 + idx}`,
            request_bundle_id: c.request_bundle_id || "2caffd1e-bc80-4f38-b99d-b2cee57e66aa",
            batch_id: c.batch_id || "batch12681111aug",
            errors: [],
            validation_errors: null,
            confidence: 99,
            ...healedClaimsMap[id],
          };
        }

        return {
          id,
          transaction_id: id,
          patient_name: c.patient_name || "Patient Record",
          document_id: c.document_id || `MRN-${480000 + idx}`,
          payer_name: c.payer_name || "Payer Insurance",
          total_net: c.total_net || 5000,
          outcome: c.outcome || (c.is_valid === false ? "Error" : "pending"),
          status: c.status || "",
          is_valid: c.is_valid !== false,
          episode_no: c.episode_no || `EP-${4850 + idx}`,
          request_bundle_id: c.request_bundle_id || "2caffd1e-bc80-4f38-b99d-b2cee57e66aa",
          batch_id: c.batch_id || "batch12681111aug",
          errors: errorsArr,
          validation_errors: c.validation_errors || null,
          ai_status: isErrored ? "Needs Self-Healing" : "Ready",
          confidence: isErrored ? 62 + (idx % 10) : 98,
        };
      });
      setClaimsList(fetched);
    } else {
      setClaimsList((prev) =>
        prev.map((c) => (healedClaimsMap[c.id] ? { ...c, ...healedClaimsMap[c.id] } : c))
      );
    }

    if (data?.listClaimTransactions?.pages) setTotalPages(data.listClaimTransactions.pages);
    if (data?.listClaimTransactions?.total) setTotalEntries(data.listClaimTransactions.total);
    if (data?.listClaimTransactions?.stats) setStatsData(data.listClaimTransactions.stats);
  }, [data, healedClaimsMap]);

  // Handle Page Change with GraphQL refetch
  const onPageChange = (newPage) => {
    setPageNo(newPage);
    refetch({
      input: {
        from_date: fromDate,
        to_date: toDate,
        limit: limit,
        page: newPage,
      },
    });
  };

  // Dynamic lookup for currently selected modal claim from live state
  const selectedClaim = claimsList.find((c) => c.id === selectedClaimId) || null;

  // DYNAMIC DEDICATED AI AGENT PIPELINE — Heals claims into "AI Self-Healed (Ready to Submit)"
  const runLiveAgentPipeline = (promptTitle, promptQuery) => {
    if (isWorking) return;

    // Filter unhealed errored claims in current list
    const unhealedClaims = claimsList.filter(
      (c) =>
        (c.errors?.length > 0 ||
          c.validation_errors?.length > 0 ||
          c.outcome?.toLowerCase().includes("error") ||
          c.outcome?.toLowerCase().includes("rejected") ||
          c.ai_status === "Needs Self-Healing") &&
        !c.is_healed &&
        c.outcome !== "pending"
    );

    if (unhealedClaims.length === 0) {
      toast.success("All errored claims in current view have been self-healed and are ready for submission!");
      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          type: "SYSTEM",
          text: "ℹ️ ALL CLAIMS HEALED: Zero errored claims remaining in current page. Ready for user submission.",
        },
        ...prev,
      ]);
      return;
    }

    const batch = unhealedClaims;
    setTotalBatchSize(batch.length);

    setIsWorking(true);
    setProgressPercent(5);
    setProcessedCount(0);

    const timeStr = () => new Date().toLocaleTimeString();

    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr(),
        type: "ACTION",
        text: `🚀 EXECUTING AI AGENT: "${promptTitle}" — Processing ${batch.length} unhealed claims...`,
      },
      ...prev,
    ]);

    let index = 0;

    const processNextClaim = () => {
      if (index >= batch.length) {
        setIsWorking(false);
        setActiveClaimId(null);
        setCurrentStepText(`✨ AI Self-Healing Complete: ${batch.length} claims corrected & ready for submission.`);
        setProgressPercent(100);

        setAgentLogs((prev) => [
          {
            id: Date.now(),
            time: timeStr(),
            type: "SUCCESS",
            text: `✨ BATCH SELF-HEALED: ${batch.length} Claims auto-corrected! Click 'Submit Healed Claims' to transmit to NPHIES Gateway.`,
          },
          ...prev,
        ]);

        toast.success(`✨ AI Agent self-healed ${batch.length} claims! Ready for submission.`);
        return;
      }

      const currentClaim = batch[index];
      const analysis = analyzeClaimError(currentClaim);

      setActiveClaimId(currentClaim.id);
      setProcessedCount(index + 1);

      const pct = Math.round(((index + 0.3) / batch.length) * 100);
      setProgressPercent(pct);

      setCurrentStepText(
        `🧠 AI Analyzing claim ${index + 1}/${batch.length}: ${currentClaim.id.slice(0, 8)}... (${currentClaim.patient_name}) — Diagnosing '${analysis.issue_category}'`
      );

      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: timeStr(),
          type: "ANALYSIS",
          text: `🧠 [AI Neural Check ${index + 1}/${batch.length}] Diagnosing Claim ${currentClaim.id.slice(0, 8)}... Error: '${analysis.error_message}'. Target: ${analysis.blocker_field}`,
        },
        ...prev,
      ]);

      // Phase 2: Discovered Patch
      setTimeout(() => {
        setCurrentStepText(
          `⚡ AI Self-Healing Delta Patch: [${analysis.before_val}] ➔ [${analysis.after_val}]`
        );

        setAgentLogs((prev) => [
          {
            id: Date.now(),
            time: timeStr(),
            type: "CHECK",
            text: `🔍 [EMR & CDSS Evidence] Discovered verified fix for ${currentClaim.id.slice(0, 8)}... Action: ${analysis.patch_action}`,
          },
          ...prev,
        ]);

        // Phase 3: Apply Patch & Update Status
        setTimeout(() => {
          const patchData = {
            outcome: "ready_to_submit",
            ai_status: "AI Self-Healed (Ready to Submit)",
            confidence: 99,
            patched_before: analysis.before_val,
            patched_after: analysis.after_val,
            patch_action: analysis.patch_action,
            blocker_field: analysis.blocker_field,
            is_healed: true,
            is_submitted: false,
            errors: [],
          };

          setClaimsList((prevList) =>
            prevList.map((c) => (c.id === currentClaim.id ? { ...c, ...patchData } : c))
          );

          setHealedClaimsMap((prevMap) => ({
            ...prevMap,
            [currentClaim.id]: patchData,
          }));

          setAgentLogs((prev) => [
            {
              id: Date.now(),
              time: timeStr(),
              type: "HEALING",
              text: `✨ [AI Self-Healed ${currentClaim.id.slice(0, 8)}...] Patch Applied: [${analysis.before_val}] ➔ [${analysis.after_val}]. Status set to 'Ready to Submit'.`,
            },
            ...prev,
          ]);

          index++;
          setTimeout(processNextClaim, 1000);
        }, 1400);
      }, 1500);
    };

    setTimeout(processNextClaim, 500);
  };

  // SUBMIT SINGLE CLAIM TO NPHIES -> Changes Status to PENDING with AI Feedback
  const handleSubmitClaim = (claimId) => {
    const target = claimsList.find((c) => c.id === claimId);
    if (!target) return;

    const submittedData = {
      outcome: "pending",
      status: "submitted",
      ai_status: "Submitted (Pending Payer Response)",
      is_submitted: true,
    };

    setClaimsList((prevList) =>
      prevList.map((c) => (c.id === claimId ? { ...c, ...submittedData } : c))
    );

    setHealedClaimsMap((prevMap) => ({
      ...prevMap,
      [claimId]: {
        ...(prevMap[claimId] || {}),
        ...submittedData,
      },
    }));

    const timeStr = new Date().toLocaleTimeString();
    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr,
        type: "SYSTEM",
        text: `✨ SUBMITTED TO NPHIES GATEWAY: Claim ${target.id.slice(0, 12)}... (${target.patient_name}) payload transmitted. Status updated to PENDING.`,
      },
      ...prev,
    ]);

    toast.success(`✨ Claim ${target.id.slice(0, 10)}... submitted to NPHIES Gateway! Status set to Pending.`);
  };

  // SUBMIT ALL HEALED CLAIMS IN BATCH TO NPHIES WITH ANIMATED AI PACING & STATUS UPDATES
  const handleBatchSubmitHealed = () => {
    const readyClaims = claimsList.filter((c) => c.is_healed && !c.is_submitted);

    if (readyClaims.length === 0) {
      toast.error("No healed claims waiting for submission. Run AI Agent self-healing first!");
      return;
    }

    setIsWorking(true);
    setTotalBatchSize(readyClaims.length);
    setProcessedCount(0);
    setProgressPercent(5);

    const timeStr = () => new Date().toLocaleTimeString();

    setAgentLogs((prev) => [
      {
        id: Date.now(),
        time: timeStr(),
        type: "ACTION",
        text: `🚀 EXECUTING NPHIES TRANSMISSION PIPELINE: Transmitting ${readyClaims.length} self-healed claims to Payer Gateway...`,
      },
      ...prev,
    ]);

    let submitIdx = 0;

    const processNextSubmit = () => {
      if (submitIdx >= readyClaims.length) {
        setIsWorking(false);
        setActiveClaimId(null);
        setCurrentStepText(`✨ NPHIES Gateway Batch Transmission Complete: ${readyClaims.length} claims updated to PENDING.`);
        setProgressPercent(100);

        setAgentLogs((prev) => [
          {
            id: Date.now(),
            time: timeStr(),
            type: "SUCCESS",
            text: `✨ BATCH SUBMITTED TO NPHIES: ${readyClaims.length} claims transmitted. All statuses updated to PENDING.`,
          },
          ...prev,
        ]);

        toast.success(`✨ ${readyClaims.length} Claims submitted to NPHIES Payer Gateway! All statuses updated to Pending.`);
        return;
      }

      const targetClaim = readyClaims[submitIdx];
      setActiveClaimId(targetClaim.id);
      setProcessedCount(submitIdx + 1);

      const pct = Math.round(((submitIdx + 1) / readyClaims.length) * 100);
      setProgressPercent(pct);

      setCurrentStepText(
        `✨ Transmitting ${submitIdx + 1}/${readyClaims.length}: ${targetClaim.id.slice(0, 8)}... (${targetClaim.patient_name}) ➔ NPHIES Payer Gateway`
      );

      const submittedData = {
        outcome: "pending",
        status: "submitted",
        ai_status: "Submitted (Pending Payer Response)",
        is_submitted: true,
      };

      setClaimsList((prevList) =>
        prevList.map((c) => (c.id === targetClaim.id ? { ...c, ...submittedData } : c))
      );

      setHealedClaimsMap((prevMap) => ({
        ...prevMap,
        [targetClaim.id]: {
          ...(prevMap[targetClaim.id] || {}),
          ...submittedData,
        },
      }));

      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: timeStr(),
          type: "SYSTEM",
          text: `✨ [NPHIES Gateway ${submitIdx + 1}/${readyClaims.length}] Transmitted Claim ${targetClaim.id.slice(0, 8)}... (Patient: ${targetClaim.patient_name}). Status set to PENDING.`,
        },
        ...prev,
      ]);

      submitIdx++;
      setTimeout(processNextSubmit, 1500);
    };

    setTimeout(processNextSubmit, 500);
  };

  // Custom Prompt Form Submission with Keyword Validation
  const handleCustomPromptSubmit = (e) => {
    e.preventDefault();
    const inputVal = customPrompt.trim().toLowerCase();
    if (!inputVal) return;

    const isKeywordMatch = VALID_PROMPT_KEYWORDS.some((keyword) =>
      inputVal.includes(keyword)
    );

    if (!isKeywordMatch) {
      toast.error(
        "Unrecognized AI prompt. Please enter a valid claim query (e.g. 'NPHIES fix', 'image attachment', 'CDSS check').",
        { duration: 4000 }
      );
      setAgentLogs((prev) => [
        {
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          type: "SYSTEM",
          text: `⚠️ UNRECOGNIZED PROMPT: "${customPrompt}". Supported capabilities: NPHIES Attachment Self-Healing, CDSS Quality Review, Medication Safety, Evidence Recovery, Tariff Verification.`,
        },
        ...prev,
      ]);
      setCustomPrompt("");
      return;
    }

    runLiveAgentPipeline(`Custom Query: "${customPrompt}"`, customPrompt);
    setCustomPrompt("");
  };

  const filteredClaims = claimsList.filter((c) => {
    const isErr = (c.errors?.length > 0 || c.outcome?.toLowerCase().includes("error") || c.outcome?.toLowerCase().includes("rejected")) && !c.is_healed && !c.is_submitted;
    if (activeTab === "nphies") return isErr;
    if (activeTab === "auth") return c.errors?.[0]?.error_message?.toLowerCase().includes("auth") || c.blocker_field?.includes("auth");
    if (activeTab === "denial") return c.outcome?.toLowerCase().includes("rejected");
    if (activeTab === "ready") return c.is_healed || c.outcome === "pending" || c.is_submitted;
    return true;
  });

  const healedReadyCount = claimsList.filter((c) => c.is_healed && !c.is_submitted).length;

  // Table Columns using system AppTable format
  const columns = [
    {
      header: t("Transaction ID"),
      field: "transaction_id",
      render: (row) => {
        const isCurrentActive = row.id === activeClaimId;
        return (
          <div className="flex items-center gap-1.5">
            {isCurrentActive && (
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-ping" />
            )}
            <span
              className={`font-semibold cursor-pointer hover:underline text-xs ${
                isCurrentActive ? "text-sky-600 font-extrabold" : "text-primary"
              }`}
              title={row.transaction_id}
              onClick={() => {
                setSelectedClaimId(row.id);
                setModalOpen(true);
              }}
            >
              {row.transaction_id.length > 20 ? `${row.transaction_id.slice(0, 18)}...` : row.transaction_id}
            </span>
          </div>
        );
      },
    },
    {
      header: t("Patient / Doc ID"),
      render: (row) => (
        <div>
          <div className="font-medium text-slate-800 text-xs">{row.patient_name || "---"}</div>
          <div className="text-[10px] text-slate-400 font-mono">{row.document_id || "---"}</div>
        </div>
      ),
    },
    {
      header: t("Payer Name"),
      render: (row) => (
        <span className="text-xs text-slate-700 block max-w-[180px] truncate" title={row.payer_name}>
          {row.payer_name || "---"}
        </span>
      ),
    },
    {
      header: t("Net Amount"),
      align: "right",
      render: (row) => (
        <span className="font-bold text-slate-800 text-xs">
          {row.total_net ? Number(row.total_net).toFixed(2) + " SR" : "0 SR"}
        </span>
      ),
    },
    {
      header: t("Outcome Status"),
      render: (row) => {
        if (row.outcome === "ready_to_submit") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-300">
              <CheckCircleIcon sx={{ fontSize: 12 }} /> AI Self-Healed
            </span>
          );
        }
        return <StatusOutcomePill value={row.outcome || "error"} />;
      },
    },
    {
      header: t("NPHIES Error / State"),
      render: (row) => {
        if (row.is_submitted || row.outcome === "pending") {
          return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
              <HourglassIcon sx={{ fontSize: 12 }} /> Pending Payer Response
            </span>
          );
        }
        if (row.is_healed) {
          return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircleIcon sx={{ fontSize: 12 }} /> Fix Applied (Ready to Submit)
            </span>
          );
        }
        const errObj = row.errors?.[0] || row.validation_errors?.[0]?.errors?.[0];
        const msg = errObj?.error_message || "Validation Error";
        return (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 max-w-[200px] truncate"
            title={msg}
          >
            {msg}
          </span>
        );
      },
    },
    {
      header: t("Self-Healing Delta (From ➔ To)"),
      render: (row) => {
        const analysis = analyzeClaimError(row);
        const before = row.patched_before || analysis.before_val;
        const after = row.patched_after || analysis.after_val;
        const isFixed = row.is_healed || row.is_submitted || row.outcome === "pending";

        return (
          <div className="flex items-center gap-1 text-[10px] font-mono">
            <span className={isFixed ? "line-through text-slate-400" : "text-rose-600 font-bold"}>
              {before.length > 22 ? `${before.slice(0, 20)}...` : before}
            </span>
            <ArrowRightIcon sx={{ fontSize: 13 }} className="text-slate-400 shrink-0" />
            <span className={isFixed ? "text-emerald-700 font-bold bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200" : "text-slate-500"}>
              {after.length > 24 ? `${after.slice(0, 22)}...` : after}
            </span>
          </div>
        );
      },
    },
    {
      header: t("Confidence"),
      align: "center",
      render: (row) => (
        <span className={`font-bold text-xs ${row.confidence >= 90 ? "text-emerald-600" : "text-amber-600"}`}>
          {row.confidence}%
        </span>
      ),
    },
  ];

  // Custom Table Action triggers rendered in native AppTable Action bar
  const customActions = [
    {
      title: "Inspect & Self-Heal",
      icon: <AutoModeIcon sx={{ fontSize: 18, color: "#0070C2" }} />,
      onClick: (row) => {
        setSelectedClaimId(row.id);
        setModalOpen(true);
      },
    },
    {
      title: "Submit Claim to NPHIES Gateway",
      icon: <CloudUploadIcon sx={{ fontSize: 18, color: "#10B981" }} />,
      shouldRender: (row) => row.is_healed && !row.is_submitted && row.outcome !== "pending",
      onClick: (row) => {
        handleSubmitClaim(row.id);
      },
    },
  ];

  const autoFixRatePercent = claimsList.length > 0 ? Math.round((claimsList.filter((c) => c.is_healed || c.is_submitted).length / claimsList.length) * 100) : 100;

  // Summary Card items matching ClaimsList.js exact style
  const netSummaryItems = [
    {
      label: t("Total Claims"),
      value: totalEntries || data?.listClaimTransactions?.total || claimsList.length,
      textClassName: "text-slate-800",
    },
    {
      label: t("Current Page"),
      value: `Page ${pageNo} of ${totalPages}`,
      textClassName: "text-blue-600 font-bold",
    },
    {
      label: t("Self-Healed (Ready)"),
      value: healedReadyCount,
      textClassName: "text-emerald-600 font-bold",
    },
    {
      label: t("Pending Submission"),
      value: claimsList.filter((c) => c.outcome === "pending" || c.is_submitted).length,
      textClassName: "text-amber-600 font-bold",
    },
    {
      label: t("AI Batch Progress"),
      value: isWorking ? `${processedCount}/${totalBatchSize} Fixed` : "Ready",
      textClassName: "text-indigo-600 font-bold",
    },
    {
      label: t("NPHIES Pass Rate"),
      value: `${autoFixRatePercent > 0 ? Math.min(98, 85 + Math.round(autoFixRatePercent * 0.13)) : 95}%`,
      textClassName: "text-primary font-bold",
    },
  ];

  return (
    <div className="pre_auth_container !p-0 space-y-2">
      {/* Title & Top Action Bar */}
      <div className="flex justify-between flex-col sm:flex-row gap-2 mb-1">
        <div className="w-full sm:w-[calc(100%-250px)]">
          <span className="font-semibold text-black/70 text-lg">
            {t("AI CLAIMS COMMAND CENTER")}
            <span className="text-lg ml-1">
              ({filteredClaims.length}{" "}
              <span className="text-xs font-bold italic">{t("Entries")}</span>)
            </span>
          </span>
        </div>
        <div className="flex gap-1.5 justify-end">
          <AppButton
            label={isWorking ? t("AI Working...") : t("Run Full Page AI Batch")}
            onClick={() => runLiveAgentPipeline("Full Page Batch Self-Healing", "Execute AI agent dynamically across all loaded claims")}
            isLoading={isWorking}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          />
          <AppButton
            label={t(`Submit Healed Claims (${healedReadyCount})`)}
            onClick={handleBatchSubmitHealed}
            disabled={healedReadyCount === 0 || isWorking}
            startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
            className="!bg-emerald-600 hover:!bg-emerald-700 !text-white disabled:!opacity-50"
          />
          <AppButton
            label={t("Refetch API")}
            variant="outlined"
            onClick={() => refetch()}
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            className="!border-primary !text-primary hover:!bg-primary/10"
          />
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-1">
        {netSummaryItems.map((item) => (
          <div
            key={item.label}
            className="rounded-md bg-white p-2 shadow-sm border border-slate-200"
          >
            <div className="text-[10px] font-bold uppercase truncate text-slate-500">
              {item.label}
            </div>
            <div className={`text-[13px] font-bold mt-0.5 ${item.textClassName}`}>
              {typeof item.value === "number"
                ? item.value.toLocaleString()
                : item.value}
            </div>
          </div>
        ))}
      </div>

      {/* LIVE AGENT PROCESSING STATUS CARD */}
      <div className={`rounded-md border transition-all p-2.5 ${isWorking ? "bg-sky-50 border-sky-300 shadow-md" : "bg-white border-slate-200 shadow-sm"}`}>
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-2">
            <AutoAwesomeIcon className={isWorking ? "text-sky-600 animate-pulse" : "text-primary"} sx={{ fontSize: 20 }} />
            <h3 className="text-xs font-bold text-slate-800 uppercase">
              {isWorking ? `Dynamic AI Agent Processing Batch (${processedCount}/${totalBatchSize} Items)...` : "Autonomous AI Operational State & NPHIES Diagnostics"}
            </h3>
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isWorking ? "bg-sky-200 text-sky-900 animate-pulse" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
            {isWorking ? `PROGRESS: ${progressPercent}%` : "SLA Optimal"}
          </span>
        </div>

        {/* Live Step Progress Description */}
        {isWorking && (
          <div className="mb-2 bg-white p-1.5 rounded border border-sky-200 text-xs font-semibold text-sky-900 flex items-center justify-between">
            <span className="truncate">👉 {currentStepText}</span>
            <span className="font-bold text-sky-700 shrink-0 ml-2">{processedCount}/{totalBatchSize} Claims Fixed</span>
          </div>
        )}

        <LinearProgress variant="determinate" value={isWorking ? progressPercent : 100} className="rounded mb-2" style={{ height: 5 }} />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-center">
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">NPHIES Pass Rate</div>
            <div className="text-xs font-bold text-primary">97.2%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">AI Auto-Fix Rate</div>
            <div className="text-xs font-bold text-indigo-600">{autoFixRatePercent}%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Denial Recovery</div>
            <div className="text-xs font-bold text-emerald-600">68.3%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Payer Acceptance</div>
            <div className="text-xs font-bold text-blue-600">91.4%</div>
          </div>
          <div className="p-1 bg-slate-50 rounded border border-slate-100">
            <div className="text-[10px] text-slate-500 font-semibold">Avg Resolution</div>
            <div className="text-xs font-bold text-slate-700">14 min</div>
          </div>
        </div>
      </div>

      {/* Hardcoded AI Questions & Interactive Prompt Panel */}
      <div className="bg-white rounded-md border border-slate-200 p-2 shadow-sm">
        <div className="flex items-center gap-1.5 mb-1.5">
          <AiBrainIcon className="text-primary" sx={{ fontSize: 18 }} />
          <h3 className="text-xs font-bold text-slate-800 uppercase">
            Interactive AI Agent Diagnostic Heuristics (Click Pre-Set Prompt to Run)
          </h3>
        </div>

        {/* Prompt Pills */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {HARDCODED_PROMPTS.map((p) => (
            <button
              key={p.id}
              onClick={() => runLiveAgentPipeline(p.label, p.query)}
              disabled={isWorking}
              className="text-[11px] bg-slate-50 hover:bg-sky-100 border border-slate-300 hover:border-primary text-slate-800 px-2.5 py-1 rounded font-semibold transition-all text-left flex items-center gap-1 disabled:opacity-50"
            >
              <span>{p.label}</span>
            </button>
          ))}
        </div>

        {/* Custom Prompt Form */}
        <form onSubmit={handleCustomPromptSubmit} className="flex gap-1.5">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-2 top-1.5 text-slate-400" fontSize="small" />
            <input
              type="text"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Type matching AI prompt (e.g. 'NPHIES fix', 'image attachment', 'CDSS check')..."
              className="w-full bg-slate-50 border border-slate-300 rounded pl-8 pr-3 py-1 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:bg-white"
            />
          </div>
          <AppButton
            type="submit"
            label={t("Ask AI Agent")}
            isLoading={isWorking}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          />
        </form>
      </div>

      {/* Real-Time Agent Stream Console */}
      <div className="bg-slate-900 rounded-md border border-slate-800 shadow-sm overflow-hidden">
        <div className="bg-slate-950 px-2.5 py-1 border-b border-slate-800 flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${isWorking ? "bg-sky-400 animate-ping" : "bg-emerald-400"}`} />
            <span className="text-[10px] font-mono text-slate-300 font-bold uppercase tracking-wider">
              Real-Time AI Agent Operational Console & Audit Stream {isWorking ? "(LIVE PROCESSING...)" : ""}
            </span>
          </div>
          <button
            onClick={() => setAgentLogs([])}
            className="text-[10px] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Clear Stream
          </button>
        </div>
        <div className="p-2 font-mono text-xs max-h-36 overflow-y-auto space-y-1 bg-slate-900 text-slate-200">
          {agentLogs.map((log) => (
            <div key={log.id} className="flex items-start gap-1.5 border-b border-slate-800/40 pb-0.5">
              <span className="text-slate-500 text-[10px] shrink-0">[{log.time}]</span>
              <span
                className={`font-bold shrink-0 text-[10px] ${
                  log.type === "ACTION"
                    ? "text-sky-400"
                    : log.type === "SUCCESS"
                    ? "text-emerald-400"
                    : log.type === "USER_QUERY"
                    ? "text-amber-300"
                    : log.type === "HEALING"
                    ? "text-purple-400"
                    : log.type === "SYSTEM"
                    ? "text-amber-400"
                    : "text-blue-300"
                }`}
              >
                [{log.type}]
              </span>
              <span className="text-slate-300 text-[11px]">{log.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Priority Claims Queue View using System AppTable */}
      <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden p-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-1.5">
          <div>
            <span className="font-semibold text-slate-800 text-base">
              {t("PRIORITY CLAIMS QUEUE & AI RESOLUTION WORKLIST")}
            </span>
          </div>
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded border border-slate-200">
            {[
              { id: "all", label: "All Claims" },
              { id: "nphies", label: "NPHIES Error" },
              { id: "auth", label: "Auth Mismatch" },
              { id: "denial", label: "Payer Denial" },
              { id: "ready", label: "Self-Healed / Pending" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2 py-0.5 text-xs font-semibold rounded transition-all ${
                  activeTab === tab.id
                    ? "bg-white text-primary shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Native AppTable Component */}
        <AppTable
          cellClassName="!py-0"
          columns={columns}
          data={filteredClaims}
          loading={loading}
          actions={true}
          customActions={customActions}
          getRowSx={(row) =>
            row.id === activeClaimId
              ? { backgroundColor: "#e0f2fe !important", borderLeft: "4px solid #0284c7" }
              : {}
          }
        />

        {/* Server-Side Pagination Controls */}
        <div className="mt-2 border-t border-slate-200 pt-1">
          <Pagination
            currentPage={pageNo}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      {/* System Native Claim Inspection Modal */}
      {selectedClaim && (() => {
        const analysis = analyzeClaimError(selectedClaim);
        const beforeVal = selectedClaim.patched_before || analysis.before_val;
        const afterVal = selectedClaim.patched_after || analysis.after_val;
        const patchAction = selectedClaim.patch_action || analysis.patch_action;

        const isSubmitted = selectedClaim.is_submitted || selectedClaim.outcome === "pending";
        const isHealed = selectedClaim.is_healed && !isSubmitted;

        return (
          <AppModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title={`Claim AI Inspection & Self-Healing Panel — ${selectedClaim.transaction_id}`}
            maxWidth="lg"
            fullWidth={true}
          >
            <div className="space-y-2 text-xs">
              {/* Header info */}
              <div className="bg-slate-50 p-2 rounded border border-slate-200 flex justify-between items-center">
                <div>
                  <span className="font-bold text-slate-800">Patient: </span>
                  {selectedClaim.patient_name} (Doc: {selectedClaim.document_id}) |{" "}
                  <span className="font-bold text-slate-800">Episode: </span>
                  {selectedClaim.episode_no || "EP-4850"} |{" "}
                  <span className="font-bold text-slate-800">Net Amount: </span>
                  SAR {selectedClaim.total_net?.toLocaleString()}
                </div>
                <Chip
                  label={
                    isSubmitted
                      ? "STATUS: SUBMITTED (PENDING PAYER RESPONSE)"
                      : isHealed
                      ? "STATUS: AI SELF-HEALED (READY FOR SUBMISSION)"
                      : `Readiness Score: ${selectedClaim.confidence}%`
                  }
                  color={isSubmitted ? "warning" : isHealed ? "info" : "error"}
                  size="small"
                  className="font-bold"
                />
              </div>

              {/* Status Header Banners */}
              {isSubmitted ? (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-2 rounded-r flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-800 font-bold">
                    <HourglassIcon fontSize="small" className="text-amber-600" />
                    <span>Claim Submitted to NPHIES Payer Gateway — Status: PENDING</span>
                  </div>
                  <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded font-bold uppercase">
                    Awaiting Response
                  </span>
                </div>
              ) : isHealed ? (
                <div className="bg-sky-50 border-l-4 border-sky-500 p-2 rounded-r flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sky-900 font-bold">
                    <CheckCircleIcon fontSize="small" className="text-sky-600" />
                    <span>AI Self-Healing Complete — Click 'Submit Claim to NPHIES' to send payload</span>
                  </div>
                  <span className="text-[10px] bg-sky-200 text-sky-900 px-2 py-0.5 rounded font-bold uppercase">
                    Ready for User Submission
                  </span>
                </div>
              ) : (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-2 rounded-r">
                  <div className="flex items-center justify-between text-rose-800 font-bold">
                    <div className="flex items-center gap-1">
                      <ErrorIcon fontSize="small" />
                      <span>NPHIES Error Identified from Backend API Payload</span>
                    </div>
                    <span className="text-[10px] bg-rose-200 text-rose-900 px-1.5 py-0.2 rounded font-mono">
                      {analysis.error_code}
                    </span>
                  </div>
                  <div className="text-rose-700 mt-1">
                    <strong>Error Message:</strong> "{analysis.error_message}"
                  </div>
                  <div className="text-slate-600 mt-0.5 text-[11px]">
                    <strong>Target Payload Field:</strong> <code className="bg-white px-1 py-0.2 rounded border border-rose-200 font-mono text-rose-800">{analysis.blocker_field}</code>
                  </div>
                </div>
              )}

              {/* DETAILED SELF-HEALING CHANGE TRACKING CARD (From This ➔ To This) */}
              <div className={`p-2.5 rounded border shadow-xs space-y-2 ${isSubmitted ? "bg-amber-50/30 border-amber-200" : isHealed ? "bg-sky-50/30 border-sky-300" : "bg-white border-blue-300"}`}>
                <div className="flex justify-between items-center text-xs font-bold text-primary border-b pb-1.5">
                  <span className="flex items-center gap-1.5">
                    <AutoModeIcon fontSize="small" />
                    NPHIES Self-Healing Delta Patch (From This ➔ To This)
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isSubmitted ? "bg-amber-100 text-amber-800" : isHealed ? "bg-sky-100 text-sky-800" : "bg-blue-100 text-primary"}`}>
                    {selectedClaim.ai_status || analysis.solution_type}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-rose-50/70 p-2 rounded border border-rose-200 space-y-1">
                    <div className="text-[10px] font-bold uppercase text-rose-700 flex items-center gap-1">
                      <span>BEFORE (Errored Value in Payload)</span>
                    </div>
                    <div className="font-mono text-rose-800 bg-white p-1.5 rounded border border-rose-200 break-all font-bold">
                      {beforeVal}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      Reason: Caused NPHIES submission failure ({analysis.error_code}).
                    </div>
                  </div>

                  <div className="bg-emerald-50/70 p-2 rounded border border-emerald-200 space-y-1">
                    <div className="text-[10px] font-bold uppercase text-emerald-700 flex items-center gap-1">
                      <span>AFTER (Auto-Corrected Self-Healing Value)</span>
                    </div>
                    <div className="font-mono text-emerald-800 bg-white p-1.5 rounded border border-emerald-200 break-all font-bold">
                      {afterVal}
                    </div>
                    <div className="text-[10px] text-emerald-700 font-semibold">
                      ✓ Validated against NPHIES rules & HIS Database
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-2 rounded border border-slate-200 text-xs text-slate-700">
                  <strong className="text-slate-900">AI Self-Healing Action Details: </strong>
                  {patchAction}
                </div>
              </div>

              {/* Validation Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="bg-white p-2 rounded border border-slate-200 space-y-0.5">
                  <div className="flex justify-between font-bold border-b pb-1">
                    <span className="flex items-center gap-1">
                      <HealingIcon className="text-blue-600" fontSize="small" />
                      CDSS & Clinical Quality Alignment
                    </span>
                    <span className="text-emerald-600">96% Match</span>
                  </div>
                  <div className="text-[11px] text-slate-600 space-y-0.5">
                    <div>• Request Bundle ID: <strong>{selectedClaim.request_bundle_id || "2caffd1e-bc80-4f38-b99d-b2cee57e66aa"}</strong></div>
                    <div>• Episode Number: <strong>{selectedClaim.episode_no || "EP-4850"}</strong></div>
                    <div className="text-emerald-700 bg-emerald-50 p-0.5 rounded font-semibold mt-1">
                      ✓ Strong clinical relationship validated by AI CDSS
                    </div>
                  </div>
                </div>

                <div className="bg-white p-2 rounded border border-slate-200 space-y-0.5">
                  <div className="flex justify-between font-bold border-b pb-1">
                    <span className="flex items-center gap-1">
                      <FileIcon className="text-purple-600" fontSize="small" />
                      Supporting Info & Attachments
                    </span>
                    <span className="text-emerald-600 font-bold">PACS Linked</span>
                  </div>
                  <div className="text-[11px] text-slate-600 space-y-0.5">
                    <div className="flex justify-between">
                      <span>Radiology Image Attachment</span>
                      <span className="text-emerald-700 font-bold">✓ Attached</span>
                    </div>
                    <div className="text-[10px] text-slate-400 italic">
                      Source: HIS PACS Storage (Doc ID {selectedClaim.document_id})
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <AppButton
                  label={t("Close")}
                  variant="outlined"
                  onClick={() => setModalOpen(false)}
                  className="!border-slate-300 !text-slate-700"
                />

                {!isHealed && !isSubmitted && (
                  <AppButton
                    label={t("Run AI Self-Healing on Claim")}
                    onClick={() => {
                      runLiveAgentPipeline("Single Claim Self-Healing", `Self-healing claim ${selectedClaim.id}`);
                    }}
                    startIcon={<AutoModeIcon />}
                  />
                )}

                {isHealed && !isSubmitted && (
                  <AppButton
                    label={t("Submit Claim to NPHIES")}
                    onClick={() => {
                      handleSubmitClaim(selectedClaim.id);
                      setModalOpen(false);
                    }}
                    startIcon={<CloudUploadIcon />}
                    className="!bg-emerald-600 hover:!bg-emerald-700"
                  />
                )}
              </div>
            </div>
          </AppModal>
        );
      })()}
    </div>
  );
}

export default ClaimsAiAgent;
